// The piece under the pointer, or the selected one. In the compact layout it
// is a tray panel with the full state in words. In the wide layout it is a
// small tooltip on the lower right corner of the square, and it has only the
// tags of the piece: a move it learned, or a condition on it. A piece with no
// tag has no tooltip. Changed 2026-10-12. The tooltip had the name, the
// square, the moves, and a note, over the square. Rob: "this popover text
// should only show tags in the lower-right corner of the piece space, for
// aditional movesets or status changes that it has". The board shows where
// the piece could move. A square of terrain keeps its words.

import { BOARD_SIZE, SQUARE_PX } from '../constants';
import { ABILITY_NAMES, comboName, effectiveAbilities, KIND_NAMES } from '../engine/abilities';
import { squareName } from '../engine/squares';
import { TERRAIN_BLURBS, TERRAIN_NAMES, terrainAt } from '../engine/terrain';
import { el } from '../lib/dom';
import { OWNER_NAMES } from '../spells/helpers';
import { type LayoutMode, layoutStore, pickVerb } from '../state/layout';
import { uiStore } from '../state/ui';
import { currentCue, subscribeView, viewedGame } from '../state/view';
import { boardOrigin } from './boardOrigin';
import { createConditionTags } from './ConditionTag';

/** The compact layout is for touch, which has no pointer to hover. */
const EMPTY_HINTS: Record<LayoutMode, { title: string; subtitle: string }> = {
  wide: { title: 'Nothing under the pointer', subtitle: 'Hover a piece to read its story.' },
  compact: { title: 'No piece picked', subtitle: 'Tap a piece to read its story.' },
};

/** A square in the bottom rows has no room under it, so its tooltip opens up from the square. */
const ROWS_THAT_OPEN_UP = 3;

/**
 * Tells the style sheet which square the tooltip reads and which way it
 * opens. It opens to the right and down from the lower right corner of the
 * square. In the right half of the board it opens to the left, and in the
 * bottom rows it opens up, so it stays on the board.
 */
const place = (panel: HTMLElement, square: number): void => {
  const origin = boardOrigin(square);
  const column = origin.x / SQUARE_PX;
  const rowFromTop = origin.y / SQUARE_PX;
  panel.style.setProperty('--tip-column', String(column));
  panel.style.setProperty('--tip-row', String(rowFromTop));
  panel.dataset.tipAlign = column < BOARD_SIZE / 2 ? 'start' : 'end';
  panel.dataset.tipDrop = rowFromTop >= BOARD_SIZE - ROWS_THAT_OPEN_UP ? 'above' : 'below';
};

export const createInspector = (): HTMLElement => {
  const title = el('h2', { className: 'inspector__title' });
  const subtitle = el('p', { className: 'inspector__subtitle' });
  const abilities = el('p', { className: 'inspector__abilities' });
  const conditions = el('ul', { className: 'inspector__conditions' });
  const note = el('p', { className: 'inspector__note' });
  const panel = el('section', { className: 'inspector' }, [
    title,
    subtitle,
    abilities,
    conditions,
    note,
  ]);

  const draw = (): void => {
    const state = viewedGame();
    const ui = uiStore.get();
    const square = ui.hovered ?? ui.selected;
    const piece = square === null ? null : state.board[square];
    const ground = square === null ? null : terrainAt(state.rules, square);
    // The wide layout shows only the tags of a piece, and nothing for a piece with none.
    panel.classList.toggle('inspector--piece', piece !== null && piece !== undefined);
    if (!piece && ground && square !== null) {
      panel.classList.remove('inspector--empty');
      place(panel, square);
      const name = TERRAIN_NAMES[ground.kind];
      title.textContent = `${name[0]?.toUpperCase()}${name.slice(1)} on ${squareName(square)}`;
      const turns = Math.ceil(ground.plies / 2);
      subtitle.textContent =
        ground.plies < 0
          ? 'For the rest of the match.'
          : `For ${turns} more turn${turns === 1 ? '' : 's'}.`;
      abilities.textContent = TERRAIN_BLURBS[ground.kind];
      conditions.replaceChildren();
      note.textContent =
        ground.kind === 'linked' && ground.link !== undefined
          ? `Linked to ${squareName(ground.link)}.`
          : '';
      return;
    }
    if (!piece || square === null) {
      panel.classList.add('inspector--empty');
      const hint = EMPTY_HINTS[layoutStore.get()];
      title.textContent = hint.title;
      subtitle.textContent = hint.subtitle;
      abilities.textContent = '';
      conditions.replaceChildren();
      note.textContent = '';
      return;
    }
    const cue = currentCue();
    if (cue.kind === 'aim') {
      note.textContent = cue.hot.includes(square)
        ? `A target for ${cue.headline}. ${pickVerb()} it.`
        : `Not a target for ${cue.headline}.`;
    } else if (cue.kind === 'move' || cue.kind === 'move-again') {
      note.textContent = cue.hot.includes(square) ? `Can move. ${pickVerb()} to pick it.` : '';
    } else {
      note.textContent = '';
    }
    panel.classList.remove('inspector--empty');
    place(panel, square);
    const combo = comboName(piece);
    const kind = KIND_NAMES[piece.kind];
    title.textContent =
      combo === kind
        ? `${OWNER_NAMES[piece.owner]} ${kind}`
        : `${OWNER_NAMES[piece.owner]} ${combo}`;
    subtitle.textContent =
      combo === kind ? `On ${squareName(square)}.` : `A ${kind} on ${squareName(square)}.`;
    const names = effectiveAbilities(piece).map((ability) => ABILITY_NAMES[ability]);
    abilities.textContent = names.length > 0 ? `Moves with ${names.join(', ')}.` : 'Cannot move.';
    const tags = createConditionTags(piece);
    panel.classList.toggle('inspector--no-tags', tags.length === 0);
    conditions.replaceChildren(...tags);
  };

  draw();
  subscribeView(draw);
  uiStore.subscribe(draw);
  layoutStore.subscribe(draw);
  return panel;
};
